import React from 'react';
import { Helmet } from "react-helmet";
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import loadable from '@loadable/component';

// components, widgets
import ContactSalesPopup from '/src/components/ContactForms/ContactSalesPopup';
import DirectoryExtension from '/src/pages/directories/helpers/DirectoryExtension';
import { DirectoryFooter, SaveSearchPopup, SavedSearchList, DirTable, TableHeader, SelectableFieldsModal, DataExportPopup, DataExportMessagePopup, TeamDetailRow, TeamDetailModal, DirectorySlidingContent } from '/src/pages/directories/components';
import { FilterModal, DirectoryFilters } from '/src/pages/directories/filters';
import { EcosystemDirectoryLandscape } from '/src/pages/directories/views';
const SuccessPopup = loadable(() => import(/* webpackChunkName: "SuccessPopup" */ '/src/components/Reusable/SuccessPopup'));

// helpers
import * as filterHelper from '/src/lib/filterHelper';
import * as ecosystemHelpers from '/src/pages/directories/helpers/ecosystemDirHelpers';
import { createRequestGuard } from '/src/pages/directories/helpers/requestGuard';
import { isPageFullySelected } from '/src/pages/directories/helpers/directoryHelpers';
import { prepareFilterParam } from '/src/lib/queryParams';
import { checkEmailStatus, getItemWebUrl, getPathFromEntityType, getSEOUrl, withBootstrapPlugin } from '/src/lib/helpers';
import toast from '/src/components/Reusable/toast';

// constants
import { WEB_URL } from '/src/lib/endpoints'
import { ALLOWED_QUERY_OPERATOR, FILTER_LAYOUT_TYPES, DIRECTORY_STARTUPS, DIRECTORY_INVESTORS, RECORD_COUNT_DEFAULT, HUBSPOT_FORM_SOURCE } from '/src/lib/constants';
import { selectableFieldsConfig } from '/src/pages/directories/constants/selectableFieldsConfig';
import { MAGNITT_ACCESS_ROLES } from '/src/lib/constants/userRoles';

// apis
import taxonomyApi from '/src/apis/taxonomyApi';
import ecosystemApi from '/src/apis/ecosystemApi';
import searchApi from "/src/apis/searchApi";


class Ecosystem extends DirectoryExtension {

    constructor(props) {
        super(props);
        this.entityType = 'INVESTOR';
        this.listGuard = createRequestGuard();

        const initState = this.initializeStateValues();
        const initTaxoState = this.initializeTaxoStateValues();

        this.state = {

            // initialize state values based on filter schema
            ...initState,
            ...initTaxoState,

            // manage counts
            count: 0,
            limit: this.props.user.is_loggedin ? RECORD_COUNT_DEFAULT.default : RECORD_COUNT_DEFAULT.loggedOut,
            page: 1,
            numoffilter: 0,

            // table data
            data: false,

            filterDefinition: {
                keyword: [
                    { name: '', type: 'list' },
                ],
                location: [],
                type: [],
                industry: [],
                stage: [],
                startup: [
                    { name: 'title', value: 'Number of startups', type: 'title' },
                    { name: 'startup', value: '0-300', min: 0, max: 300, type: 'range' }
                ],
            },
            arrInvestedInTypeFilterOptions: [
                { id: "ALL", name: "All Types" },
                { id: "STARTUP", name: "Startups" },
                { id: "FUND", name: "Funds" }
            ],
            // sorting
            sortlist: [
                { name: 'Latest', order: 'desc', field: 'created_at' },
                { name: 'No. of Investments: High to Low', order: 'desc', field: 'number_of_investments' },
                { name: 'No. of Investments: Low to High', order: 'asc', field: 'number_of_investments' },
                { name: 'No. of Exits: High to Low', order: 'desc', field: 'number_of_exits' },
                { name: 'No. of Exits: Low to High', order: 'asc', field: 'number_of_exits' },
                { name: 'Estimated Deployed Capital: High to Low', order: 'desc', field: 'estimated_deployed_capital' },
                { name: 'Estimated Deployed Capital: Low to High', order: 'asc', field: 'estimated_deployed_capital' },
            ],

            // validation

            // seo
            location_seo: '',
            type_seo: '',
            locations_seo: '',
            types_seo: '',
            table_title: 'Investors & Enablers',
            page_title: 'Discover Emerging Venture Markets Investors | MAGNiTT',
            page_description: 'Looking for who has made investments in startups and companies by Venture Capital (VC), Angel Investor, Institutional Fund or a  Family Office in the Middle East, Africa, Pakistan and Turkey? Browse through the investors in our directory list on MAGNiTT.',
            page_url: '/investors',
            showSEOSection: false,

            // selectable fields
            isSubscribedUser: false,
            selectableFields: selectableFieldsConfig.investor,

            // saved search
            isSearchSaveable: false,
            isSaveSearchDialogVisible: false,
            savedLists: [],
            updateSavedList: false,

            // data export
            arrSelectedEntities: [],

            // contact sales popup source
            contactSalesPopUpSource: HUBSPOT_FORM_SOURCE.investor_dir_locked_section,

            // directory views
            isTableView: true,
            isLandscapeView: false,
            currentTab: 'TableView',

            // directory views (data)
            directoryNetworkMap: {
                filter: {},
                limit: 9,
                networkMapData: {
                    data: []
                },
                refresh: 0
            },
            removeSkeletonTable: false,

        }

        this.newFilters = {
            keyword: {
                'key': 'keyword',
                'op': ALLOWED_QUERY_OPERATOR.equal,
                value: null
            },
            location: {
                'key': 'hq_id',
                'op': ALLOWED_QUERY_OPERATOR.equal,
                value: null
            },
            industry: {
                'key': 'industry',
                'op': ALLOWED_QUERY_OPERATOR.equal,
                value: null
            },
            stage: {
                'key': 'development_stage',
                'op': ALLOWED_QUERY_OPERATOR.equal,
                value: null
            },
            type: {
                'key': 'type_id',
                'op': ALLOWED_QUERY_OPERATOR.equal,
                value: null
            },
            startup_num: {
                'key': 'number_of_startup_investments',
                'op': ALLOWED_QUERY_OPERATOR.btw,
                value: null
            },


            // following: {
            //     'key': 'user_following',
            //     'op': ALLOWED_QUERY_OPERATOR.equal,
            //     value: null
            // },
            geoFocusLocation: {
                'key': 'geo_focus',
                'op': ALLOWED_QUERY_OPERATOR.equal,
                value: null
            },
            regionFocusLocation: {
                'key': 'region_focus_filter',
                'op': ALLOWED_QUERY_OPERATOR.equal,
                value: null
            },
            region: {
                'key': 'region',
                'op': ALLOWED_QUERY_OPERATOR.equal,
                value: null
            },
            numberOfFunds: {
                key: 'number_of_funds_filter',
                op: ALLOWED_QUERY_OPERATOR.equal,
                value: null
            },
            investedInTypeFilter: {
                key: 'invested_in_type_filter',
                op: ALLOWED_QUERY_OPERATOR.equal,
                value: []
            },
            investmentRegion: {
                'key': 'investment_region_filter',
                'op': ALLOWED_QUERY_OPERATOR.equal,
                value: null
            },
            investmentLocation: {
                key: 'investment_location_filter',
                op: ALLOWED_QUERY_OPERATOR.equal,
                value: []
            },
            investmentFundingStage: {
                key: 'investment_funding_stage_filter',
                op: ALLOWED_QUERY_OPERATOR.equal,
                value: null
            },
            investedInPrimaryIndustry: {
                key: 'investedin_primary_industry_filter',
                op: ALLOWED_QUERY_OPERATOR.equal,
                value: null
            },
            investmentVehicle: {
                key: 'investment_vehicle_filter',
                op: ALLOWED_QUERY_OPERATOR.equal,
                value: []
            },
            investment_date_from: {
                key: 'investment_date_filter',
                op: ALLOWED_QUERY_OPERATOR.gte,
                value: null
            },
            investment_date_to: {
                key: 'investment_date_filter',
                op: ALLOWED_QUERY_OPERATOR.lte,
                value: null
            }
        };

        this.handleNumofStartupChange = this.handleNumofStartupChange.bind(this)
        this.handleChange = this.handleChange.bind(this)
        this.changeLimit = this.changeLimit.bind(this);
        this.changeOrder = this.changeOrder.bind(this);
        this.getSavedSearches = this.getSavedSearches.bind(this);
        this.renderSavedLists = this.renderSavedLists.bind(this);
        this.clearFilter = this.clearFilter.bind(this);
        this.isComponentMounted = false;

    }

    componentDidMount() {
        this.isComponentMounted = true;
        this.checkSubscribedUser();
        this.getTaxonomyData();
    }

    componentWillUnmount() {
        this.isComponentMounted = false;
        this.listGuard.cancel();
    }

    getTaxonomyData = () => {
        const taxoPromises = this.getTaxonomyPromises();
        Promise.all(taxoPromises).then(() => {
            this.checkSavedFilters();
        })
    }

    /**
     * Check if the url is following this structure /en-:iso_code/investors or /en-:iso_code/investors/:investor_type
     * then handle the SEO filters and section
     * structure e.g /en-ae/investors, /en-ae/investors/corporate-accelerator, /en-ae/investors/corporate-accelerator?any_string
     */
    checkSavedFilters = () => {
        const url = this.props.match.url
        if (/\/([en]){2}([-]){1}([a-z]{2})\/(investors)/.test(url)) {
            this.applySEOFilters()
            // set URL for SEO
            this.updateState({ page_url: url })
        } else {
            const url = this.props.location.search
            const newSavedFilters = filterHelper.convertToFilterString(url)

            if (newSavedFilters) {
                this.applySavedFilters(newSavedFilters)
            } else {
                this.filterValues('');
            }
        }
    }

    prepNewFilters = (oldFilters) => {
        const filterOptions = {
            investedInTypeFilter: this.state.arrInvestedInTypeFilterOptions
        }
        const finalFilters = ecosystemHelpers.prepNewFilters(oldFilters, this.newFilters, filterOptions);
        return finalFilters;
    }

    Login(event) {
        const headerLogin = document.getElementById('headerLogin');
        if (headerLogin) {
            headerLogin.click();
        }
    }

    filterValues = (filter = "") => {

        const { isPageChanged } = filter;
        delete filter.isPageChanged;

        let { limit, page } = this.state;
        const tempFilter = { ...filter };
        const sort = (tempFilter.order && tempFilter.order.field) ? `${tempFilter.order.field}^:${tempFilter.order.order}` : 'number_of_investments^:desc';
        const newFilters = this.prepNewFilters(tempFilter);

        // If page hasn't changed, set it to 1
        // This will help when page was set to > 1 and filters were applied
        if (!isPageChanged) {
            page = 1;

            this.updateState({
                page: page
            });
        }

        if (newFilters) {
            //make search saveable if any filters are applied
            if (this.props.user.is_loggedin)
                this.updateState({ isSearchSaveable: true })
        } else {
            this.updateState({ isSearchSaveable: false })
        }

        // update the list of selected fields/columns
        this.handleSelectableFieldsUpdate(newFilters);

        // add the selectable columns that the user chose
        let extraFields = [];
        this.state.selectableFields.map((field) => {
            if (field.checked && field.allowAccess) {
                if (field.keyName === 'dry_powder') {
                    extraFields.push('dry_powder', 'fund_of_fund_target_amount');
                } else {
                    extraFields.push(field.keyName);
                }
            }
        })

        // Update state only when there is change in filters
        if (!isEqual(this.state.directoryNetworkMap.filter, filter)) {
            this.updateState(prevState => ({
                directoryNetworkMap: {
                    ...prevState.directoryNetworkMap,
                    filter: cloneDeep(filter),
                    refresh: prevState.directoryNetworkMap.refresh + 1
                }
            }));
        }

        const { id: requestId, signal } = this.listGuard.start();
        this.updateState({ loadingDirPage: true });

        ecosystemApi.getInvestors(extraFields, newFilters, limit, page, sort, signal)
            .then(response => {
                if (this.listGuard.isStale(requestId)) return;

                let investors = response.investors;
                for (let investor of investors) {
                    investor.checked = false;
                }

                this.updateState({
                    data: investors,
                    count: response.count,
                    loadingDirPage: false,
                    // keep current selection on page change so rows ticked while loading aren't dropped
                    ...(isPageChanged ? {} : { arrSelectedEntities: [] })
                });

                this.updatePageTitle();
                this.calculateFiltersCount();
            })
            .catch(() => {
                if (this.listGuard.isStale(requestId)) return;
                this.updateState({ loadingDirPage: false });
            });
    }

    showContactForm = () => {
        this.updateState({ contactSalesPopUpSource: HUBSPOT_FORM_SOURCE.investor_dir_locked_section })
        withBootstrapPlugin('modal', ($) => $('#contactFormModal').modal({
            backdrop: 'static', keyboard: true, show: true
        }));
    }

    /**
     * Update the selected directory columns/fields
     * @param {String} filters
     */
    handleSelectableFieldsUpdate = (filters = "") => {
        const tempSelectableFields = [...this.state.selectableFields];

        const arrTemp = tempSelectableFields.map(fieldItem => {
            if (fieldItem && fieldItem.filter && filters.includes(fieldItem.filter)) {
                fieldItem.checked = true;
            }

            return fieldItem;
        });

        this.updateState({
            selectableFields: arrTemp
        });
    }

    applySEOFilters = () => {
        let pageTitle, pageDescription
        let isoCode, isoCodePromise
        let typeCode, typePromise
        let location = ''
        let investorType = ''
        let filterParams = this.props.match.params
        let tempFilter = this.state.tempFilter
        let thisObj = this;

        if ('iso' in filterParams) {
            isoCode = filterParams.iso

            isoCodePromise = taxonomyApi.getLocationByISOCode(isoCode).then(
                response => {
                    if (response.length > 0) {
                        tempFilter.location.push({ id: response[0].id, name: response[0].name })
                        location = response[0]
                    }
                }
            )
        }

        // create code for the investor types to create the urls
        const types_seo = this.state.types.map(type => {
            const code = type.name.toLowerCase().replace(/ /g, "-").replace(/[()]/g, "")
            type.code = code
            return type
        })

        if ('type' in filterParams) {
            typeCode = filterParams.type
            const type = types_seo.filter(type => type.code == typeCode)

            if (type.length > 0 && type[0]?.id) {
                tempFilter.type.push({ id: type[0].id, name: type[0].name })
                investorType = type[0]
            }
        }

        // Get MENA countries for SEO section
        const menaCountriesPromise = taxonomyApi.getMENACountries().then(
            response => {
                thisObj.updateState({
                    locations_seo: response,
                });
            }
        )

        let filterPromises = [isoCodePromise, typePromise, menaCountriesPromise];

        Promise.all(filterPromises).then(() => {
            if (!isEmpty(investorType)) {
                pageTitle = `${investorType.name} in ${location.name}`
                pageDescription = `This list of ${investorType.name}s in ${location.name} provides data on the venture investment trends. Insights about top ${investorType.name}s, their investments and exits, the portfolios of these companies, their management team, and recent news are also included.`
            } else {
                pageTitle = `Investors in ${location.name}`
                pageDescription = `This list of investors in ${location.name} provides data on the venture and funding trends in the country. Insights about top investors, their investments and exits, the portfolios of these companies, their management team, and recent news are also included.`
            }

            this.updateState({
                tempFilter,
                location_seo: location,
                types_seo: types_seo,
                type_seo: investorType,
                table_title: pageTitle,
                page_title: pageTitle,
                page_description: pageDescription,
                showSEOSection: true
            }, () => {
                this.calculateFiltersCount()
                this.filterValues(this.state.tempFilter)
            })
        });

    }

    updatePageTitle = () => {
        let { tempFilter, count } = this.state
        let tableTitle = 'Investors'
        let pageTitle = 'Discover MENA Investors'

        if (tempFilter.location.length == 1) {
            tableTitle = pageTitle = `${tableTitle} in ${tempFilter.location[0].name}`
        }

        if (tempFilter.type.length == 1 && tempFilter.location.length == 1) {
            tableTitle = pageTitle = `${tempFilter.type[0].name} in ${tempFilter.location[0].name}`
        }

        pageTitle = `${pageTitle} - ${count.toLocaleString()} | MAGNiTT`

        this.updateState({ table_title: tableTitle, page_title: pageTitle });
    }

    applySavedFilters = (rawSavedFilters) => {
        const { id: requestId, signal } = this.listGuard.start();
        // the Claimed/Unclaimed filter is retired: drop it from older saved searches so it no longer filters invisibly
        const savedFilters = rawSavedFilters.split('^,').filter((token) => token.split('^')[0] !== 'owned_by').join('^,');
        let { limit, page, locations, types, industrys, stages, regions, arrInvestedInTypeFilterOptions, investmentFundingStages,
            investmentVehicles
		 } = this.state;

        let sort = 'number_of_investments^:desc';

        let filterArr = filterHelper.decodeFilters(savedFilters);
        let tempObj = {};
        let basic = [];

        // saved searches created before the Investment Type -> Investment Vehicle rename used
        // investment_type_filter; that criterion can't be translated into the new investmentVehicle
        // taxonomy (different value space), so surface the drop instead of silently discarding it
        const hasLegacyInvestmentTypeFilter = filterArr.some(item => item.hasOwnProperty('investment_type_filter'));
        const hasInvestmentVehicleFilter = filterArr.some(item => item.hasOwnProperty('investment_vehicle_filter'));
        if (hasLegacyInvestmentTypeFilter && !hasInvestmentVehicleFilter) {
            toast.info(
                'This saved search used the old Investment Type filter, which has been replaced by Investment Vehicle. That filter criterion has been removed — please reselect it if needed.',
                'legacy-investment-type-filter'
            );
        }

        let keywords = filterHelper.getFilterByKey(filterArr, 'keyword', [], true);
        let locationFilter = filterHelper.getFilterByKey(filterArr, 'hq_id', locations)
        let typeFilter = filterHelper.getFilterByKey(filterArr, 'type_id', types)
        let industryFilter = filterHelper.getFilterByKey(filterArr, 'industry', industrys)
        let stageFilters = filterHelper.getFilterByKey(filterArr, 'development_stage', stages);
        let startup = filterHelper.getRangeFilterByKey(filterArr, 'number_of_startup_investments');
        const geoFocusFilter = filterHelper.getFilterByKey(filterArr, 'geo_focus', locations);
        const regionFocusFilter = filterHelper.getFilterByKey(filterArr, 'region_focus_filter', regions);
        const investmentLocationFilter = filterHelper.getFilterByKey(filterArr, 'investment_location_filter', locations);
        const investmentRegionFilter = filterHelper.getFilterByKey(filterArr, 'investment_region_filter', regions);
        const regionFilter = filterHelper.getFilterByKey(filterArr, 'region', regions);
        const numberOfFunds = filterHelper.getFilterByKey(filterArr, 'number_of_funds_filter', [], true);
        const strInvestedInTypeFilter = filterHelper.getFilterByKey(filterArr, 'invested_in_type_filter', arrInvestedInTypeFilterOptions);
        const strInvestmentVehicleFilter = filterHelper.getFilterByKey(filterArr, 'investment_vehicle_filter', investmentVehicles);
        const investedInPrimaryIndustryFilter = filterHelper.getFilterByKey(filterArr, 'investedin_primary_industry_filter', industrys);
        const investmentFundingStageFilters = filterHelper.getFilterByKey(filterArr, 'investment_funding_stage_filter', investmentFundingStages);
        let investmentDate = filterHelper.getRangeFilterByKey(filterArr, 'investment_date_filter');

        // investment date filter
        if (investmentDate) {
            let fromDate, toDate;

            if (investmentDate.min?.investment_date_filter || investmentDate.max?.investment_date_filter) {
                fromDate = investmentDate.min && investmentDate.min.investment_date_filter ? new Date(investmentDate.min.investment_date_filter) : "";
                toDate = investmentDate.max && investmentDate.max.investment_date_filter ? new Date(investmentDate.max.investment_date_filter) : "";
            } else {
                fromDate = investmentDate.min ? new Date(investmentDate.min) : "";
                toDate = investmentDate.max ? new Date(investmentDate.max) : "";
            }

            investmentDate = { from: fromDate, to: toDate };
        }

        if (startup && startup.min && startup.max) {
            tempObj.startup_minValue = parseInt(startup.min);
            tempObj.startup_maxValue = parseInt(startup.max);
        }

        let tempFilter = {
            keyword: keywords != undefined ? keywords : [],
            basic: basic,
            region: regionFilter,
            location: locationFilter,
            geoFocusLocation: geoFocusFilter,
            regionFocusLocation: regionFocusFilter,
            investmentRegion: investmentRegionFilter,
            investmentLocation: investmentLocationFilter,
            investmentFundingStage: investmentFundingStageFilters,
            investedInPrimaryIndustry: investedInPrimaryIndustryFilter,
            type: typeFilter,
            industry: industryFilter,
            stage: stageFilters,
            startup: startup,
            numberOfFunds,
            investedInTypeFilter: strInvestedInTypeFilter,
            investmentVehicle: strInvestmentVehicleFilter,
            investment_date: investmentDate ? investmentDate : '',
            order: { field: "number_of_investments", order: "desc" }
        }

        this.updateState({ tempFilter, ...tempObj })

        // add the selectable columns that the user chose
        let extraFields = [];
        this.state.selectableFields.map((field) => {
            if (field.checked && field.allowAccess) {
                if (field.keyName === 'dry_powder') {
                    extraFields.push('dry_powder', 'fund_of_fund_target_amount');
                } else {
                    extraFields.push(field.keyName);
                }
            }
        })

        // Saved filter for charts and map view
        this.updateState(prevState => ({
            directoryNetworkMap: {
                ...prevState.directoryNetworkMap,
                filter: cloneDeep(tempFilter),
                refresh: prevState.directoryNetworkMap.refresh + 1
            }
        }));

        ecosystemApi.getInvestors(extraFields, savedFilters, limit, page, sort, signal)
            .then(response => {
                if (this.listGuard.isStale(requestId)) return;

                let investors = response.investors;
                this.calculateFiltersCount()
                this.updateState({
                    data: investors,
                    count: response.count,
                    isSearchSaveable: true,
                    isShowExportBtn: true
                });
                this.updatePageTitle()
            }).catch(err => {
                if (this.listGuard.isStale(requestId)) return;
                console.log("Error:", err)
            })
    }

    setParentState = (state, callback) => {
        this.updateState({
            ...this.state,
            ...state
        }, callback)
    }

    toggleSaveSearchDialog = (shouldOpen) => {
        if (!checkEmailStatus(this.props.user.email_status)) {
            return
        }

        this.updateState({
            isSaveSearchDialogVisible: shouldOpen
        })
    }

    onSaveSearch = () => {
        this.getSavedSearches()
        this.updateState({
            isSearchSaveable: false, updateSavedList: false
        })
    }

    updateFilters = () => {
        this.filterValues(this.state.tempFilter)
    }

    getDataSort = () => {
        const { tempFilter } = this.state;

        if (isEmpty(tempFilter.order)) {
            return 'number_of_investments^:desc';
        } else {
            return `${tempFilter.order.field}^:${tempFilter.order.order}`;
        }
    }

    renderCountryTypesSection = () => {
        let { location_seo, type_seo, types_seo } = this.state
        let list = []
        const countryName = location_seo.name
        const isoCode = location_seo.iso_code

        if (type_seo) {
            const typeId = type_seo.id
            types_seo.map(
                type => {
                    if (type.id != typeId) {
                        let link = getSEOUrl('investors', isoCode, type.code)
                        list.push(<li><a href={link}>{`${type.name} in ${countryName}`}</a></li>)
                    }
                }
            )
        } else {
            types_seo.map(
                type => {
                    let link = getSEOUrl('investors', isoCode, type.code)
                    list.push(<li><a href={link}>{`${type.name} in ${countryName}`}</a></li>)
                }
            )
        }

        return <ul style={{ fontSize: 14 }}>
            <li><h1>Investors by Type</h1></li>
            {list}
        </ul>
    }

    renderTypeByCountrySection = () => {
        let { location_seo, type_seo, locations_seo } = this.state
        let list = []

        if (type_seo) {
            const typeName = type_seo.name
            const typeCode = type_seo.code

            locations_seo.map(
                location => {
                    if (location.id != location_seo.id) {
                        let link = getSEOUrl('investors', location.iso_code, typeCode)
                        list.push(<li><a href={link}>{`${typeName} in ${location.name}`}</a></li>)
                    }
                }
            )
        } else {
            locations_seo.map(
                location => {
                    if (location.id != location_seo.id) {
                        let link = getSEOUrl('investors', location.iso_code)
                        list.push(<li><a href={link}>{`Investors in ${location.name}`}</a></li>)
                    }
                }
            )
        }

        return <ul style={{ fontSize: 14 }}>
            <li><h1>Investors by Country</h1></li>
            {list}
        </ul>
    }

    renderStartups = () => {
        const list = []
        list.push(<li><h1>Startups</h1></li>)
        DIRECTORY_STARTUPS.forEach(startup => {
            list.push(<li><a href={startup.link}>{startup.name}</a></li>)
        })
        return list
    }

    renderInvestors = () => {
        const list = []
        list.push(<li><h1>Investors</h1></li>)
        DIRECTORY_INVESTORS.forEach(startup => {
            list.push(<li><a href={startup.link}>{startup.name}</a></li>)
        })
        return list
    }

    renderSavedLists = (search) => {
        const searchList = Array.isArray(search) ? search : [];
        searchList.map((search, j) => {
            const currentUrl = window.location.pathname + window.location.search;
            const filtersUrl = filterHelper.convertToUrl(search.filters);

            search.url = `/${search.entity_type}${filtersUrl}&search_id=${search.id}`;
            const decodeUrl = currentUrl.replaceAll('%3E', '>').replaceAll('%3C', '<')
            search.isSelected = (search.url === decodeUrl);
        })
        this.updateState({ savedLists: searchList });
    }

    getSavedSearches = async (page = "", limit = "") => {
        const sort = 'updated_at^:DESC';
        let { user } = this.props;
        let directory = 'investors'

        const filters = prepareFilterParam([{
            key: 'entity_type',
            op: ALLOWED_QUERY_OPERATOR.equal,
            value: [(directory)]
        }])
        const response = await searchApi.getSavedSearch(user.user_id, filters, limit, page, sort)
        this.renderSavedLists(response.records);
        return response;
    }

    changeTab = (currentTab) => {
        let obj = {};
        obj.isTableView = false;
        obj.isLandscapeView = false
        obj[currentTab] = true;
        obj.currentTab = currentTab.replace("is", '');
        this.updateState(obj, () => {
            this.calculateFiltersCount();
        });

        if (typeof window.jQuery !== 'undefined') {
            $(function () {
                // remove chart tooltips when tab is changed
                $(".d3-tooltip").remove();

                // remove landscape tooltips when tab is changed
                $(".popover").remove();
            })
        }
    }

    renderTabViews = () => {
        return (
            <div className="btn-group investor-view-btn"
                role="group"
                aria-label="Button group with nested dropdown">
                <button
                    onClick={() => this.changeTab('isTableView')}
                    type="button"
                    className={`btn btn-sm ${this.state.isTableView ? 'btn-main' : 'btn-main-outline'}`}
                >
                    <i className="fa fa-table"></i>
                    Table View
                </button>
                <button onClick={() => this.changeTab('isLandscapeView')}
                    type="button"
                    className={`btn  btn-sm ${this.state.isLandscapeView ? 'btn-main' : 'btn-main-outline'}`}
                >
                    <i className="fa fa-th-large"></i>
                    Landscape View
                </button>
            </div>
        );
    }

    render() {

        let { isSearchSaveable, isSaveSearchDialogVisible, arrSelectedEntities, isTableView, isLandscapeView, currentTab, isSubscribedUser } = this.state
        const isSeoDirectory = this?.props?.match?.params?.iso;

        let content = null;
        let tableContent = {};

        tableContent.rows = [];
        tableContent.pinnedRow = [];

        if (this.state.data !== false && this.state.data.length) {

            this.state.data.map((investor, i) => {

                let ecosystemTypePath = null;
                if (investor.ecosystem_type) {
                    ecosystemTypePath = `${investor.ecosystem_type.toLowerCase()}s`;
                }

                const entityProfileUrl = getItemWebUrl(ecosystemTypePath, investor.id, investor.code)

                let image;
                if (!isEmpty(investor.logo_data)) {
                    image = encodeURI(investor.logo_data.path);
                    image = <a>
                        <div className="startupLogo" style={{ backgroundImage: `url('${image}')`, }}></div>
                    </a>
                } else {
                    image = <a href={`/${entityProfileUrl}`}><div className='startupLogo'><h2>{investor.name.substr(0, 1).toUpperCase()}</h2></div></a>
                }

                let checkBox = '';
                let nameHeader = 'Name';
                const roles = this.props.user.roles;
                if (isSubscribedUser) {
                    const isInvestorSelected = arrSelectedEntities.includes(investor.id);

                    checkBox =
                        <div className="custom-control custom-checkbox">
                            <input type="checkbox" className="custom-control-input" id="connect" checked={isInvestorSelected} />
                            <label htmlFor="connect" className="custom-control-label" onClick={(event) => this.handleCheckBox(event, investor.id)}></label>
                        </div>;

                    // checkbox to select all investors for data export
                    nameHeader = <div className='d-flex align-items-end'>
                        <div className="custom-control custom-checkbox">
                            <input type="checkbox" className="custom-control-input" id="select-startup-all" checked={isPageFullySelected(this.state.data, arrSelectedEntities)} />
                            <label htmlFor="select-startup-all" className="custom-control-label" onClick={this.handleCheckAllBoxes}></label>
                        </div>
                        <div>Name</div>
                    </div>;
                }

                let nameRow = (
                    <div className="d-flex align-items-center">
                        {checkBox}
                        <a className="d-flex align-items-center">
                            {image}
                            <div className="flex-column" style={{ marginLeft: 10, marginTop: -4 }} >
                                <div className="startup-name" data-action="expand-slider" data-entityid={investor.id} data-ecosystemtype={investor.ecosystem_type.toLowerCase()}>
                                    {investor.name}
                                </div>
                            </div>
                        </a>
                    </div>
                );

                // send to table
                // Note // don't change property names - these are used for css styles
                tableContent.header = [nameHeader];
                let fieldData = [{ name: nameRow }];

                // check selectable fields
                this.state.selectableFields.map((field) => {
                    if (field.checked && field.allowAccess) {

                        const itemField = investor[field.keyName];
                        let tempData = "-";

                        tableContent.header.push(field.fieldName);

                        if (field.keyName === 'dry_powder') {
                            const dryPowder = Number(investor.dry_powder);
                            const target = Number(investor.fund_of_fund_target_amount);
                            if (dryPowder > 0 && target > 0) {
                                const formatted = ecosystemHelpers.formatDirectoryAmount(dryPowder);
                                const pct = ((dryPowder / target) * 100).toFixed(1);
                                tempData = <span style={{ whiteSpace: 'nowrap' }}>{formatted}<span className="amount--estimated"> ({pct}%)</span></span>;
                            } else {
                                tempData = <span>Undisclosed</span>;
                            }
                        } else if (ecosystemHelpers.hasDirectoryFieldValue(itemField)) {
                            switch (field.keyName) {
                            case "description":
                                tempData = itemField;
                                break;

                            case "hq_data":
                            case "type_data":
                                tempData = itemField.name;
                                break;

                            case "industry_data":
                            case "geo_focus_data":
                            case "development_stage_data":
                                let arrTemp = [];
                                itemField.map((item) => {
                                    if (item && item.name) {
                                        arrTemp.push(item.name);
                                    }
                                });

                                tempData =
										arrTemp.length > 0
										    ? arrTemp.join(", ")
										    : "-";
                                break;

                            case "estimated_deployed_capital":
                            case "fund_of_fund_total_amount_raised":
                                tempData = ecosystemHelpers.formatDirectoryAmount(itemField);
                                break;

                            case "median_deal_size":
                                tempData = ecosystemHelpers.formatDirectoryAmount(itemField);
                                break;

                            case "ticket_size_range":
                            case "median_valuation":
                                tempData = ecosystemHelpers.formatDirectoryRangeAmount(itemField);
                                break;

                            case "first_date_invested":
                                tempData = ecosystemHelpers.formatDirectoryYear(itemField);
                                break;

                            case "last_investment":
                                if (!isEmpty(itemField)) {
                                    let strEntityUrl = "";

                                    if (
                                        itemField &&
											itemField.type === "FUND"
                                    ) {
                                        const strPath =
												getPathFromEntityType(
												    itemField.manager_entity_type
												);
                                        // The entity code is not being sent from the backend; the URL will be /type/id
                                        strEntityUrl = `/${strPath}/${itemField.manager_entity_id}`;
                                    } else {
                                        const strPath =
												getPathFromEntityType(
												    itemField.type
												);
                                        const URL = getItemWebUrl(
                                            strPath,
                                            itemField.id,
                                            itemField.code
                                        );
                                        strEntityUrl = `/${URL}`;
                                    }

                                    let startupImage = itemField.logo;
                                    if (startupImage) {
                                        startupImage = (
                                            <div
                                                className="startupLogo"
                                                style={{
                                                    backgroundImage: `url('${startupImage}')`
                                                }}
                                            ></div>
                                        );
                                    } else {
                                        startupImage = (
                                            <div className="startupLogo">
                                                <h2>
                                                    {itemField.name
                                                        .substr(0, 1)
                                                        .toUpperCase()}
                                                </h2>
                                            </div>
                                        );
                                    }

                                    tempData = (
                                        <a
                                            className="d-flex align-items-center"
                                            key={itemField.id}
                                            href={strEntityUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {startupImage}
                                            <div style={{ marginLeft: 9 }}>
                                                <div className="startup-name">
                                                    {itemField.name}
                                                </div>
                                            </div>
                                        </a>
                                    );
                                }
                                break;

                            case "number_of_investments":
                            case "number_of_funds":
                            case "number_of_startup_investments":
                            case "number_of_lps":
                            case "number_of_exits":
                            case "number_of_funds_invested_in":
                            case "number_of_active_entities":
                            case "investments_ttm":
                                // paid rows blurred / not available
                                if (!isSubscribedUser) {
                                    tempData = "paidData";
                                } else {
                                    tempData = itemField;
                                }
                                break;

                            case "team_data":
                                if (!isEmpty(itemField)) {
                                    tempData = (
                                        <TeamDetailRow
                                            entityName={investor.name}
                                            teamData={itemField}
                                            setState={(s, c) => {
                                                this.updateState(s, c);
                                            }}
                                        />
                                    );
                                }
                                break;

                            default:
                                tempData = "-";
                            }
                        }

                        fieldData.push({ [field.keyName]: tempData });

                    }
                })

                tableContent.rows.push(fieldData);
            })

            content = (
                <DirTable
                    content={tableContent}
                    user={this.props.user}
                    entityType={this.entityType}
                    setState={(s, c) => { this.updateState(s, c) }}
                />
            )

        }

        let tableHeader = <TableHeader
            // header
            type="ecosystem"
            count={this.state.count}
            title={this.state.table_title}
            limit={this.state.limit}
            page={this.state.page}
            // sort by
            selectedItem={this.state.sort}
            sortList={this.state.sortlist}
            changeOrder={this.changeOrder}
            // data request
            isShowExportBtn={(arrSelectedEntities.length > 0) || (this.state.numoffilter > 0) || this.state.isShowExportBtn}
            isSubscribedUser={isSubscribedUser}
            getFilters={() =>
                this.prepNewFilters(this.state.tempFilter)
            }
            Login={this.Login}
            // saved search
            savedLists={this.state.savedLists}
            getSavedSearches={this.getSavedSearches}
            isSearchSaveable={isSearchSaveable}
            toggleSaveSearchDialog={() =>
                this.toggleSaveSearchDialog(true)
            }
            // selectable fields
            selectableFields={true}
            renderTabViews={this.renderTabViews}
            isTableView={this.state.isTableView}
            isLandscapeView={this.state.isLandscapeView}
            // for chart export
            setParentState={(s, c) => { this.updateState(s, c) }}
            showContactForm={this.showContactForm}
            isSeoDirectory={isSeoDirectory}
        />

        // todo
        // we can pass this/state directly instead of doing it in an ugly way
        const filterProps = {
            passedState: this.state,
            user: this.props.user,
            directory: 'ecosystem',
            filterLayoutType: FILTER_LAYOUT_TYPES.ecosystem,
            location: this.state.location,
            locations: this.state.locations,
            tempFilter: this.state.tempFilter,
            handleRemoveFilter: this.handleRemoveFilter,
            handleSelectFilter: this.handleSelectFilter,
            handleChangeInput: this.handleChangeInput,
            handleKeywordEnter: this.handleKeywordEnter,
            filterValues: this.filterValues,
            handleClearAllFilters: this.handleClearAllFilters,
            keyword: this.state.keyword,
            onKeywordChange: this.onKeywordChange,
            clearFilter: this.clearFilter,
            numoffilter: this.state.numoffilter,
            setParentState: this.setParentState,
            handleChange: this.handleChange,
            isSearchSaveable: isSearchSaveable,
            applyFilters: this.handleSearchFilters,
            toggleSaveSearchDialog: this.toggleSaveSearchDialog,
            industry: this.state.industry,
            industrys: this.state.industrys,
            calculateFiltersCount: this.calculateFiltersCount,
            isLoggedIn: this.props.user.is_loggedin,
            types: this.state.types,
            handleItemSelect: this.handleItemSelect,
            Login: this.Login,
            filterDefinition: this.state.filterDefinition,
            handleChangeText: this.handleChangeText,
            stage: this.state.stage,
            stages: this.state.stages,
            updateFilters: this.updateFilters,

            // geo focus filter
            geoFocusLocation: this.state.geoFocusLocation,
            geoFocusList: this.state.geoFocusLocations,

            // region focus filter
            regionFocusLocation: this.state.regionFocusLocation,
            regionFocusList: this.state.regionFocusLocations,

            // region filter
            region: this.state.region,
            regionsList: this.state.regions,
            getRegionLocations: this.getRegionLocations,

            // invested in type filter
            investedInTypeFilter: this.state.investedInTypeFilter,
            arrInvestedInTypeFilterOptions: this.state.arrInvestedInTypeFilterOptions,

            // view indicators
            isTableView: this.state.isTableView,
            isLandscapeView: this.state.isLandscapeView,

            // investment vehicle filter
            investmentVehicle: this.state.investmentVehicle,
            investmentVehicles: this.state.investmentVehicles,
            // isSeoDirectory
            isSeoDirectory
        }

        return (
            <div className="footer-margin-offset">
                <Helmet
                    title={this.state.page_title}
                    link={[
                        {
                            rel: 'canonical',
                            href: `${WEB_URL}${this.state.page_url}`
                        }
                    ]}
                    meta={[
                        {
                            name: 'description',
                            content: this.state.page_description
                        },
                        {
                            name: 'abstract',
                            content: this.state.page_description
                        },
                        {
                            property: 'og:type',
                            content: 'article'
                        },
                        {
                            property: 'og:url',
                            content: `${WEB_URL}${this.state.page_url}`
                        },
                        {
                            property: 'og:title',
                            content: this.state.page_title
                        }
                    ]}
                    script={[
                        {
                            'type': 'application/ld+json',
                            'innerHTML': `{
                                "@context": "http://schema.org",
                                "@type": "BreadcrumbList",
                                "itemListElement": [{
                                    "@type": "ListItem",
                                    "position": 1,
                                    "name": "investors",
                                    "item": "${WEB_URL}${this.state.page_url}"
                                }]
                            }`
                        }
                    ]}
                />

                {isSaveSearchDialogVisible &&
                    <SaveSearchPopup
                        close={() => { this.toggleSaveSearchDialog(false) }}
                        user={this.props.user}
                        filters={this.prepNewFilters(this.state.tempFilter)}
                        pageName="investors"
                        onSave={this.onSaveSearch}
                        setParentState={(s, c) => { this.updateState(s, c) }}
                    />
                }

                <section className="directory content-box">
                    <div className="row">
                        <div className="column-full rightside">

                            {tableHeader}
                            <DirectoryFilters
                                {...filterProps}
                                currentTab={currentTab}
                                showLegend={isTableView}
                            />

                            <div className={`${isTableView ? "" : "d-none"} table-row-container`}>
                                {content}
                                <DirectoryFooter
                                    // data
                                    count={this.state.count}
                                    data={this.state.data}
                                    loadingDirPage={this.state.loadingDirPage}
                                    allowNavigateWhileLoading
                                    user={this.props.user}
                                    page={this.state.page}
                                    limit={this.state.limit}
                                    tempFilter={this.state.tempFilter}
                                    // func
                                    clearFilter={this.clearFilter}
                                    filterValues={this.filterValues}
                                    setState={(s, c) => { this.updateState(s, c) }}
                                    removeSkeletonTable={this.state.removeSkeletonTable}
                                    tabIsVisible={isTableView}
                                />
                            </div>

                            <div className={`${isLandscapeView ? "" : "d-none"}`}>
                                <EcosystemDirectoryLandscape
                                    data={this.state.directoryNetworkMap}
                                    tempFilter={this.state.tempFilter}
                                    clearFilter={this.clearFilter}
                                    user={this.props.user}
                                    setState={(s, c) => { this.updateState(s, c) }}
                                    amplitudeTrack={this.amplitudeTrack}
                                    tabIsVisible={isLandscapeView}
                                />
                            </div>

                            {this.state.showSEOSection &&
                                <section className="seo_container">
                                    <div className="row">
                                        <div className="col">
                                            {this.renderCountryTypesSection()}
                                        </div>
                                        <div className="col">
                                            {this.renderTypeByCountrySection()}
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col" style={{ paddingTop: 20, paddingBottom: 10 }}>
                                            {isEmpty(this.state.type_seo) ?
                                                <p>This list of investors in {this.state.location_seo.name} provides data on the venture and funding trends in the country. Insights about top investors, their investments and exits, the portfolios of these companies, their management team, and recent news are also included.</p>
                                                :
                                                <p>This list of {this.state.type_seo.name} in {this.state.location_seo.name} provides data on the venture investment trends. Insights about top {this.state.type_seo.name}, their investments and exits, the portfolios of these companies, their management team, and recent news are also included.</p>
                                            }
                                        </div>
                                    </div>
                                </section>
                                ||
                                <div className={`directory-footer  ${isSeoDirectory ? "directory-seo-container" : ""}`}>
                                    <div className="directory-footer__left seo_container">
                                        <div className="directory-footer__startups">
                                            {this.renderStartups()}
                                        </div>
                                        <div className="directory-footer__investors">
                                            {this.renderInvestors()}
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>

                    </div>

                    <DirectorySlidingContent
                        // data
                        entityData={this.state.data}
                        entityType={this.entityType}
                        user={this.props.user}
                        // functions
                        setState={(s, c) => { this.updateState(s, c) }}
                    />

                </section>

                <SuccessPopup
                    msg={this.state.popupSuccessMsg}
                    handleClose={() => { }}
                />

                {this.isComponentMounted &&
					<DataExportPopup
					    dataSetType="ECOSYSTEM"
					    directory="Ecosystem"
					    user={this.props.user}
					    isSubscribedUser={isSubscribedUser}
					    selectableFields={[...this.state.selectableFields]}
					    recordsCount={this.state.count}
					    sort={this.state.sort}
					    sortList={this.state.sortlist}
					    selectedEntities={this.state.arrSelectedEntities}
					    getFilters={() =>
					        this.prepNewFilters(this.state.tempFilter)
					    }
					    getSort={() => this.getDataSort()}
					    showSuccessPopup={this.showSuccessPopup}
					/>
                }

                <DataExportMessagePopup />

                <SelectableFieldsModal
                    type="investor"
                    user={this.props.user}
                    isSubscribedUser={isSubscribedUser}
                    selectableFields={this.state.selectableFields}
                    setState={(s, c) => { this.updateState(s, c) }}
                    updateResults={() => this.filterValues(this.state.tempFilter)}
                />

                {this.isComponentMounted &&
					<SavedSearchList
					    updateSavedList={this.state.updateSavedList}
					    user={this.props.user}
					    getSavedSearches={this.getSavedSearches}
					/>
                }

                <TeamDetailModal
                    entityName={this.state.selectedTeamEntity}
                    teamData={this.state.selectedTeamData}
                />

                {/* used for rendering popup when user clicks contact sales button */}
                {this.isComponentMounted &&
                	<ContactSalesPopup page={this.state.contactSalesPopUpSource} />
                }

                <FilterModal {...filterProps} currentTab={currentTab} />

            </div>
        )

    }
}

const mapStateToProps = (state) => ({
    user: state.userReducer
})

export default connect(mapStateToProps)(withRouter(Ecosystem));
